import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '../local-storage.service';
import { CognitoService, IUser } from '../service/cognito.service';
import { SystemMessageService } from '../service/system-message.service';
import { ThemeService } from '../service/theme.service';
import { ValidationService } from '../service/validation.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  // Central state for all login flows (login, reset, MFA, validation)
  user: IUser = { email: '', password: '', code: '', name: '' };
  action: string | null = null;
  returnUrl: string | null = '';

  loading = false;
  errorMessage = '';
  fallback = false;
  invalidEmail = false;
  emptyEmail = false;
  showAlertStrong = false;
  showAlertWeak = false;

  view: LoginView = LoginView.Login;
  readonly LoginView = LoginView;

  // Form state fields used by child view components
  email = '';
  resetCode = '';
  resetNewPassword = '';
  requestNewPassword = '';
  requestNewPasswordConfirmation = '';
  confirmationCode = '';

  MFACode = '';
  MFACodeSMS = '';
  MFACodeTOTP = '';
  mfaChecked = false;
  nextTotpPage = false;
  phone_number = '';

  showPassword = false;
  showPasswordConfirmation = false;
  showOldPassword = false;

  // Password requirement flags shared by reset/new-password views
  charactersChecked = false;
  lowerCaseChecked = false;
  upperCaseChecked = false;
  oneDigitChecked = false;
  specialCharChecked = false;
  showCheckBoxTip = false;
  showCheckBoxTipResetPw = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    public cognitoService: CognitoService,
    private validate: ValidationService,
    public theme: ThemeService,
    private systemMessageService: SystemMessageService
  ) {}

  async ngOnInit(): Promise<void> {
    this.cognitoService.confirmValidUser();
    this.action = this.route.snapshot.paramMap.get('action');
    this.returnUrl = this.route.snapshot.paramMap.get('return');

    this.cognitoService.qrCodeImgActive = false;

    this.cognitoService.wrongMFACode$.subscribe(() => {
      this.systemMessageService.selectRibbon('danger', 'wrongMFACode');
    });
    this.cognitoService.MFASignUpSuccess$.subscribe(() => {
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    });

    // Block UI until we know whether we should auto-redirect or show a form
    this.loading = true;
    await this.tryAutoLogin();
    this.setInitialViewFromAction();
    this.loading = false;
  }

  private async tryAutoLogin(): Promise<void> {
    if (this.action !== null) return;
    const response = await this.cognitoService.getUser().catch(() => null);
    if (response) {
      const attributes = response.attributes;
      const userName = `${attributes.given_name} ${attributes.family_name}`;
      this.localStorageService.addItem('user', userName);
      this.localStorageService.addItem(
        'client',
        response.attributes['custom:client_id']
      );
      await this.router.navigate(['/dashboard']);
    }
  }

  private setInitialViewFromAction(): void {
    switch (this.action) {
      case 'change-pw':
        this.setView(LoginView.NewPassword);
        this.cognitoService.getUser().then((result) => {
          this.user.email = result.attributes.email;
        });
        break;
      case 'sign-up-MFA':
        this.setView(LoginView.MfaSignUpQr);
        this.getMFARegisterCodeTOTP();
        this.cognitoService.getUser().then((result) => {
          this.cognitoService.getUserMFAPreference();
          this.phone_number = '*******' + result.attributes.phone_number.slice(-4);
        });
        break;
      case 'authenticate-phone':
        this.setView(LoginView.PhoneValidation);
        this.cognitoService.sendSMS();
        this.systemMessageService.selectRibbon('info', 'textMessageSendToUser');
        break;
      default:
        this.setView(LoginView.Login);
        break;
    }
  }

  setView(view: LoginView): void {
    // Reset visibility toggles when switching views to avoid leaking state
    if (view === LoginView.Login) {
      this.showPassword = false;
      this.showPasswordConfirmation = false;
      this.showOldPassword = false;
    }
    if (view === LoginView.ForgotPasswordEmail) {
      this.email = '';
    }
    if (view === LoginView.ForgotPasswordReset) {
      this.resetCode = '';
      this.resetNewPassword = '';
      this.showCheckBoxTipResetPw = false;
      this.showAlertStrong = false;
      this.showAlertWeak = false;
    }
    if (view === LoginView.NewPassword) {
      this.showPassword = false;
      this.showPasswordConfirmation = false;
      this.showOldPassword = false;
    }
    // Assign last to ensure resets apply before render
    this.view = view;
  }

  async onLoginSubmit(): Promise<void> {
    if (!this.user.email || !this.user.password) {
      this.systemMessageService.selectRibbon('danger', 'passwordUsernameEmpty');
      return;
    }

    this.loading = true;
    const signedIn = await this.cognitoService.signIn(this.user);
    if (!signedIn) {
      this.loading = false;
      this.systemMessageService.selectRibbon('danger', 'passwordUsernameIncorrect');
      this.setView(LoginView.Login);
      return;
    }

    switch (signedIn) {
      case 'change-password':
        this.loading = false;
        this.setView(LoginView.NewPassword);
        return;
      case 'MFA-required':
        this.loading = false;
        this.view = LoginView.MfaSignIn;
        return;
      default:
        break;
    }

    try {
      const response: any = await this.cognitoService.getUser();
      if (!response) {
        await this.delay();
        this.loading = false;
        this.setView(LoginView.Login);
        this.systemMessageService.selectRibbon('danger', 'passwordUsernameIncorrect');
        return;
      }

      const attributes = response.attributes;
      const userName = `${attributes.given_name} ${attributes.family_name}`;
      this.localStorageService.addItem('user', userName);
      this.localStorageService.addItem('client', attributes['custom:client_id']);

      if (!attributes.email_verified || attributes.email_verified === 'false') {
        this.user.email = attributes.email;
        this.fallback = true;
        await this.cognitoService.resendValidationCode();
        this.loading = false;
        this.setView(LoginView.EmailValidation);
        this.systemMessageService.selectRibbon('info', 'emailValidationForCognito');
        return;
      }

      if (this.localStorageService.getItem('user').length !== 0) {
        this.cognitoService.getKeys();
        await this.delay();
        await this.router.navigate(['/dashboard']);
      }
    } catch (error) {
      console.error('Error: ', error);
      await this.delay();
      this.systemMessageService.selectRibbon('danger', 'passwordUsernameIncorrect');
    } finally {
      this.loading = false;
    }
  }

  onForgotPasswordClick(): void {
    this.email = '';
    this.resetCode = '';
    this.resetNewPassword = '';
    this.setView(LoginView.ForgotPasswordEmail);
  }

  async sendForgotPasswordEmail(): Promise<void> {
    if (!this.isValidEmail(this.email)) {
      this.systemMessageService.selectRibbon('danger', 'loginForgotPasswordAlertEmailInvalid');
      return;
    }
    const sendEmail = await this.cognitoService.forgotPassword(this.email);
    if (sendEmail) {
      this.resetCode = '';
      this.resetNewPassword = '';
      this.showCheckBoxTipResetPw = false;
      this.showAlertStrong = false;
      this.showAlertWeak = false;
      this.setView(LoginView.ForgotPasswordReset);
    } else {
      this.setView(LoginView.Login);
      this.systemMessageService.selectRibbon('danger', 'cognitoLoginErrorMessage');
    }
  }

  async submitPasswordReset(): Promise<void> {
    if (!this.validate.strongPassword.test(this.resetNewPassword)) {
      this.showCheckBoxTipResetPw = true;
      return;
    }
    const passwordReset = await this.cognitoService.resetPassword(
      this.email,
      this.resetCode,
      this.resetNewPassword
    );
    if (passwordReset) {
      this.setView(LoginView.Login);
      this.resetCode = '';
      this.resetNewPassword = '';
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    } else {
      this.systemMessageService.selectRibbon('danger', 'cognitoLoginErrorMessage');
    }
  }

  async confirmNewPasswordClick(): Promise<void> {
    if (
      !this.validate.strongPassword.test(this.requestNewPassword) ||
      this.requestNewPassword !== this.requestNewPasswordConfirmation
    ) {
      this.showCheckBoxTip = true;
      return;
    }

    if (this.action === 'change-password') {
      this.cognitoService.challengeName = '';
    }

    const passwordChanged = await this.cognitoService.changePassword(
      this.user,
      this.requestNewPassword
    );

    if (passwordChanged) {
      const filterComponent = sessionStorage.getItem('filterComponent');
      if (filterComponent === 'userEdit') {
        sessionStorage.removeItem('from');
        sessionStorage.setItem('action', 'updated');
        await this.router.navigate(['/user-edit']);
        return;
      }
      this.user.password = '';
      this.setView(LoginView.EmailValidation);
      this.systemMessageService.selectRibbon('info', 'emailValidationForCognito');
      return;
    }

    this.setView(LoginView.Login);
    this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
  }

  async emailValidate(): Promise<void> {
    const response = await this.cognitoService.validateEmail(this.confirmationCode);
    if (response === undefined) {
      if (!this.user.email.includes('@')) {
        this.user.email = '';
        this.user.password = '';
      }
      if (this.fallback) {
        this.fallback = false;
        await this.router.navigate(['/dashboard']);
      }
      this.setView(LoginView.Login);
      this.systemMessageService.selectRibbon('success', 'validationEmailSuccess');
    } else {
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
    }
  }

  async phoneNumberValidate(): Promise<void> {
    const response = await this.cognitoService.validatePhoneNumber(this.confirmationCode);
    if (response === undefined) {
      await this.router.navigate(['/user-edit']);
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    } else {
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
    }
  }

  async MFASignIn(): Promise<void> {
    this.loading = true;
    const response = await this.cognitoService.MFASignIn(this.user, this.MFACode);
    if (response !== 'no-MFA-sign-in') {
      try {
        const user = await this.cognitoService.getUser();
        if (user) {
          this.localStorageService.addItem(
            'user',
            `${user.attributes.given_name} ${user.attributes.family_name}`
          );
          this.localStorageService.addItem('client', user.attributes['custom:client_id']);
          this.cognitoService.getKeys();
          await this.router.navigate(['/dashboard']);
        }
      } catch (error) {
        console.error('Error in getting user : ', error);
      }
    }
    this.loading = false;
  }

  submitMFACodeClick(): void {
    if (this.mfaChecked) {
      this.cognitoService.signUpMFACodeSMS(this.MFACodeSMS);
    } else {
      this.cognitoService.singUpMFACodeTOTP(this.MFACodeTOTP);
    }
  }

  getMFARegisterCodeTOTP(): void {
    this.cognitoService.getQrCode();
  }

  backToEditPage(): void {
    this.router.navigate(['/user-edit']);
  }

  cancelHandler(): void {
    if (this.returnUrl === '' || this.returnUrl === null) {
      this.router.navigate(['/login']);
    } else {
      this.router.navigate(['/' + this.returnUrl]);
    }
  }

  validatePasswordInput(password: string, forReset = false): void {
    if (!forReset) {
      this.showCheckBoxTip = true;
      this.showAlertStrong = false;
      this.showAlertWeak = false;

      if (this.validate.strongPassword.test(password)) {
        this.showAlertStrong = true;
        this.showCheckBoxTip = false;
      } else if (password.length >= 1) {
        this.showAlertWeak = true;
        this.showCheckBoxTip = true;
      } else {
        this.showCheckBoxTip = false;
      }
    } else {
      this.showCheckBoxTipResetPw = password.length > 0;
      this.showAlertStrong = false;
      this.showAlertWeak = false;
      if (this.validate.strongPassword.test(password)) {
        this.showAlertStrong = true;
      } else if (password.length >= 1) {
        this.showAlertWeak = true;
      }
    }
    this.charactersChecked = password.length >= 8;
    this.lowerCaseChecked = /[a-z]/.test(password);
    this.upperCaseChecked = /[A-Z]/.test(password);
    this.oneDigitChecked = /\d/.test(password);
    this.specialCharChecked = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  checkEmail(): void {
    this.emptyEmail = this.email === '';
    if (this.emptyEmail) {
      this.invalidEmail = false;
      return;
    }
    this.invalidEmail = !this.isValidEmail(this.email);
  }

  togglePasswordVisibility(from: 'password' | 'confirmPassword' | 'oldPassword'): void {
    if (from === 'password') {
      this.showPassword = !this.showPassword;
    }
    if (from === 'confirmPassword') {
      this.showPasswordConfirmation = !this.showPasswordConfirmation;
    }
    if (from === 'oldPassword') {
      this.showOldPassword = !this.showOldPassword;
    }
  }

  isValidEmail(email: string): boolean {
    if (!email) return false;
    const validation = new RegExp('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$');
    return validation.test(email);
  }

  async delay(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), 3000);
    });
  }
}

export enum LoginView {
  Login = 'login',
  ForgotPasswordEmail = 'forgot-password-email',
  ForgotPasswordReset = 'forgot-password-reset',
  NewPassword = 'new-password',
  EmailValidation = 'email-validation',
  PhoneValidation = 'phone-validation',
  MfaSignIn = 'mfa-sign-in',
  MfaSignUpQr = 'mfa-sign-up-qr',
  MfaSignUpCode = 'mfa-sign-up-code',
}
